import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  me as fetchMe,
  signout as apiSignout,
  updateProfile as apiUpdateProfile,
  verifyOtp as apiVerifyOtp,
  type AuthUser,
  type OtpVerifyResult,
} from './authApi.js';
import { getAuthClientConfig } from './config.js';
import { clearSessionCaches } from './sessionCache.js';

/**
 * Estado de sesión compartido de la familia de apps (extraído de speak).
 *
 * On mount it calls `GET /api/auth/me` once to hydrate the current user
 * (guarded against React StrictMode's double-mount in dev). Passwordless: la
 * vía de entrada es `otpVerify` (registra y loguea con el mismo código) o, en
 * speak, un login social (`completeSignIn`, mismo contrato de respuesta);
 * `signout` clears the user.
 *
 * La UI de los guards (splash, error de conexión, paths de login/home) la
 * aporta cada app vía la prop `ui` — el package no trae branding.
 */

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signout: () => Promise<void>;
  /**
   * OTP passwordless: verifica el código, setea el user y devuelve además
   * `isNewUser` para que el caller dirija signups al onboarding.
   */
  otpVerify: (email: string, code: string) => Promise<OtpVerifyResult>;
  /**
   * Applies the result of another sign-in that answers like `otpVerify` (native
   * Google / Apple, speak plan 50) the same way: session caches cleared, user
   * set. The caller routes `isNewUser` to onboarding, as after `otpVerify`.
   */
  completeSignIn: (result: OtpVerifyResult) => void;
  /** Re-hidrata el user desde `me()`. */
  refresh: () => Promise<void>;
  /**
   * El boot no pudo resolver la sesión por un fallo de transporte / 5xx (NO un
   * 401). La cookie puede seguir siendo válida: el guard muestra "sin conexión
   * + reintentar" en vez de patear a /login. `false` cuando hay user o cuando
   * el boot resolvió limpio (incluido un 401 → deslogueado real).
   */
  bootError: boolean;
  /** Reintenta la hidratación del boot (lo llama la pantalla de error). */
  retry: () => Promise<void>;
}

/** UI y rutas que cada app aporta a los guards del package. */
export interface AuthUiConfig {
  /** Splash de marca a pantalla completa mientras hidrata la sesión. */
  splash: ReactNode;
  /** Pantalla "sin conexión" con botón de reintento (boot falló por transporte). */
  connectionError: (retry: () => void) => ReactNode;
  /** Ruta del login de la app (destino del guard sin sesión). */
  loginPath: string;
  /** Ruta home de la app (destino post-login / RedirectIfAuthed). */
  homePath: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const AuthUiContext = createContext<AuthUiConfig | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  ui: AuthUiConfig;
}

/** Número de intentos de hidratación del boot antes de declarar `bootError`. */
const HYDRATE_MAX_ATTEMPTS = 3;
/** Backoff entre intentos (ms): se multiplica por el nº de intento → 600, 1200. */
const HYDRATE_BACKOFF_MS = 600;

/** Zona horaria del browser (IANA), con fallback UTC. */
function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function AuthProvider({ children, ui }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(false);
  // Guard against StrictMode's double effect run in dev: hydrate me() once.
  const hydratedRef = useRef(false);

  // Hidratación del boot con reintentos. `me()` devuelve `null` en 401
  // (deslogueado REAL → user=null, sin reintentar) pero THROWS ante fallo de
  // transporte / 5xx. Esos NO son "deslogueado": tratarlos como tal pateaba a
  // /login a todos los que recargaban durante un deploy del backend o un hipo
  // de red, con la cookie todavía válida. Ante un throw reintentamos con
  // backoff corto y, si no vuelve, exponemos `bootError` para que el guard
  // ofrezca reintento en vez de redirigir. Estable (deps []) → seguro como
  // valor del contexto y dep del effect.
  const hydrate = useCallback(async (): Promise<void> => {
    setLoading(true);
    setBootError(false);
    // Nativo: hidratamos el Bearer token desde el storage durable ANTES del
    // primer `me()`, para que el request lleve `Authorization: Bearer` y la
    // sesión sobreviva al reinicio de la app. No-op en web (sesión en cookie).
    await getAuthClientConfig().native?.loadToken();
    for (let attempt = 1; attempt <= HYDRATE_MAX_ATTEMPTS; attempt += 1) {
      try {
        const current = await fetchMe(); // null = 401 (deslogueado); throw = red/5xx
        setUser(current);
        setBootError(false);
        setLoading(false);
        return;
      } catch {
        if (attempt < HYDRATE_MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, attempt * HYDRATE_BACKOFF_MS));
        }
      }
    }
    // Agotados los reintentos: error de arranque, NO deslogueado (no tocamos
    // `user`, que sigue en su valor previo — null en el boot inicial).
    setBootError(true);
    setLoading(false);
  }, []);

  useEffect(() => {
    // `hydratedRef` already guarantees a single fetch across StrictMode's
    // mount→cleanup→remount cycle (the ref persists on the same fiber). We
    // deliberately do NOT add a `cancelled` flag tied to cleanup: under
    // StrictMode the first run owns the only fetch, and a cleanup-set flag
    // would strand it — `setLoading(false)` would never fire and the app
    // would hang on a blank loading state forever.
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    void hydrate();
  }, [hydrate]);

  // Captura silenciosa de timezone (Plan 29 F4.0 / D11 de speak): sin esto,
  // los emails de engagement con ventana "hora local" (racha, win-back) tratan
  // a todos como UTC. Una vez por user por carga de la SPA; fail-soft (un
  // PATCH fallido no afecta la sesión).
  const tzSyncedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user) return;
    if (tzSyncedForRef.current === user.id) return;
    const tz = browserTimeZone();
    if (!tz || tz === user.timezone) {
      tzSyncedForRef.current = user.id;
      return;
    }
    tzSyncedForRef.current = user.id;
    void apiUpdateProfile({ name: user.name, locale: user.locale, timezone: tz })
      .then((fresh) => setUser(fresh))
      .catch(() => {
        // Silencioso a propósito: la tz se reintenta en la próxima carga.
      });
  }, [user]);

  // Cambio de identidad (otp/signout) → vaciar los cachés de sesión. El logout
  // no recarga la página, así que sin esto un segundo usuario en la misma
  // sesión SPA vería datos del anterior.
  const signout = useCallback(async (): Promise<void> => {
    try {
      await apiSignout();
    } finally {
      clearSessionCaches();
      setUser(null);
    }
  }, []);

  const otpVerify = useCallback(async (email: string, code: string): Promise<OtpVerifyResult> => {
    const result = await apiVerifyOtp(email, code);
    clearSessionCaches();
    setUser(result.user);
    return result;
  }, []);

  const completeSignIn = useCallback((result: OtpVerifyResult): void => {
    clearSessionCaches();
    setUser(result.user);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const current = await fetchMe();
    setUser(current);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signout,
      otpVerify,
      completeSignIn,
      refresh,
      bootError,
      retry: hydrate,
    }),
    [user, loading, signout, otpVerify, completeSignIn, refresh, bootError, hydrate],
  );

  return (
    <AuthContext.Provider value={value}>
      <AuthUiContext.Provider value={ui}>{children}</AuthUiContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}

/** Config de UI de los guards (interno al package; lo consumen los guards). */
export function useAuthUi(): AuthUiConfig {
  const ctx = useContext(AuthUiContext);
  if (!ctx) {
    throw new Error('useAuthUi must be used within <AuthProvider>');
  }
  return ctx;
}
